import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  CreditCardIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  HomeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  clientPortalService,
  clearClientSession,
  getCachedClient,
  getClientToken,
  PortalCertificate,
  PortalInvoice,
  PortalJob,
  PortalOverview,
  PortalQuote,
  PortalSite,
} from '../services/clientPortalService';
import PortalShell, { PortalNavGroup } from '../components/Layout/PortalShell';
import { Badge, BadgeTone, Button, PageHeader } from '../components/ui';

type Tab = 'overview' | 'quotes' | 'jobs' | 'certificates' | 'invoices' | 'sites';

const VALID_TABS: Tab[] = ['overview', 'quotes', 'jobs', 'certificates', 'invoices', 'sites'];

const money = (n: number | null | undefined) => (n == null ? '—' : `£${Number(n).toFixed(2)}`);

const formatDate = (d: string | null | undefined, opts?: Intl.DateTimeFormatOptions) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', opts || { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (d: string | null | undefined) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  SENT: 'info',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  CHANGES_REQUESTED: 'warning',
  EXPIRED: 'neutral',
  PENDING: 'neutral',
  SCHEDULED: 'info',
  ASSIGNED: 'purple',
  EN_ROUTE: 'info',
  ON_SITE: 'success',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  INVOICED: 'info',
  CANCELLED: 'danger',
  PAID: 'success',
  OVERDUE: 'danger',
  VALID: 'success',
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] || 'neutral';
  return <Badge tone={tone}>{status.replace(/_/g, ' ')}</Badge>;
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get('tab') as Tab) || 'overview';
  const tab: Tab = VALID_TABS.includes(tabParam) ? tabParam : 'overview';

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [client, setClient] = useState<any>(() => getCachedClient());
  const [overview, setOverview] = useState<PortalOverview | null>(null);
  const [quotes, setQuotes] = useState<PortalQuote[]>([]);
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  const [certs, setCerts] = useState<PortalCertificate[]>([]);
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [sites, setSites] = useState<PortalSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!getClientToken()) navigate('/client/login', { replace: true });
  }, [navigate]);

  const loadTab = useCallback(async (next: Tab) => {
    setLoading(true);
    setError(null);
    try {
      if (next === 'overview') {
        const [me, ov] = await Promise.all([
          clientPortalService.getMe(),
          clientPortalService.getOverview(),
        ]);
        setClient(me.client);
        setOverview(ov);
      } else if (next === 'quotes') setQuotes(await clientPortalService.listQuotes());
      else if (next === 'jobs') setJobs(await clientPortalService.listJobs());
      else if (next === 'certificates') setCerts(await clientPortalService.listCertificates());
      else if (next === 'invoices') setInvoices(await clientPortalService.listInvoices());
      else if (next === 'sites') setSites(await clientPortalService.listSites());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTab(tab);
  }, [loadTab, tab]);

  const handleLogout = () => {
    clearClientSession();
    navigate('/client/login', { replace: true });
  };

  const company = overview?.company;

  const groups: PortalNavGroup[] = useMemo(
    () => [
      {
        title: 'WORKSPACE',
        items: [
          {
            name: 'Overview',
            href: '/client/dashboard',
            icon: HomeIcon,
            match: (p) => p === '/client/dashboard' && !searchParams.get('tab'),
          },
          {
            name: 'Quotes',
            href: '/client/dashboard?tab=quotes',
            icon: DocumentTextIcon,
            match: () => tab === 'quotes',
          },
          {
            name: 'Jobs',
            href: '/client/dashboard?tab=jobs',
            icon: BriefcaseIcon,
            match: () => tab === 'jobs',
          },
          {
            name: 'Certificates',
            href: '/client/dashboard?tab=certificates',
            icon: DocumentCheckIcon,
            match: () => tab === 'certificates',
          },
          {
            name: 'Invoices',
            href: '/client/dashboard?tab=invoices',
            icon: CreditCardIcon,
            match: () => tab === 'invoices',
          },
        ],
      },
      {
        title: 'PROPERTIES',
        items: [
          {
            name: 'Sites',
            href: '/client/dashboard?tab=sites',
            icon: BuildingOffice2Icon,
            match: () => tab === 'sites',
          },
        ],
      },
    ],
    [tab, searchParams],
  );

  const tabCopy: Record<Tab, { title: string; subtitle: string }> = {
    overview: {
      title: 'Overview',
      subtitle: `Welcome back${client?.name ? `, ${client.name.split(' ')[0]}` : ''}. Here's a snapshot of your account.`,
    },
    quotes: { title: 'Quotes', subtitle: 'Review and respond to quotes from your contractor.' },
    jobs: { title: 'Jobs', subtitle: 'Track scheduled, active, and completed work.' },
    certificates: { title: 'Certificates', subtitle: 'Safety certificates and compliance records.' },
    invoices: { title: 'Invoices', subtitle: 'Outstanding and historical invoices.' },
    sites: { title: 'Sites', subtitle: 'Properties and locations we service for you.' },
  };

  return (
    <PortalShell
      brand={{
        name: company?.name || 'Portal',
        logoUrl: company?.logo || null,
        initial: (company?.name || 'C')[0],
      }}
      user={{
        name: client?.name,
        email: client?.email,
        subtitle: 'Client',
      }}
      groups={groups}
      onLogout={handleLogout}
    >
      <div className="page-container">
        <PageHeader title={tabCopy[tab].title} subtitle={tabCopy[tab].subtitle} />

        {loading && <div className="ui-portal-notice">Loading…</div>}
        {error && <div className="ui-error-card"><p>{error}</p></div>}

        {!loading && !error && tab === 'overview' && overview && (
          <div className="ui-portal-stack">
            <div className="ui-overview-grid">
              <OverviewCard title="Open jobs" value={String(overview.counts.openJobs)} />
              <OverviewCard title="Quotes" value={String(overview.counts.quotes)} />
              <OverviewCard title="Outstanding invoices" value={String(overview.counts.outstandingInvoices)} />
            </div>
            <div className="ui-overview-grid ui-overview-grid-2col">
              <section className="ui-portal-card">
                <h2>Next appointment</h2>
                {overview.nextAppointment ? (
                  <div className="ui-portal-card-body">
                    <p className="ui-portal-heading">{overview.nextAppointment.title || 'Scheduled job'}</p>
                    <p className="ui-portal-muted">{formatDateTime(overview.nextAppointment.scheduledDate)}</p>
                    <p className="ui-portal-muted">
                      {overview.nextAppointment.assignedEngineer?.name
                        ? `Engineer: ${overview.nextAppointment.assignedEngineer.name}`
                        : 'Engineer not yet assigned'}
                    </p>
                    <div className="ui-portal-card-footer">
                      <StatusPill status={overview.nextAppointment.status} />
                    </div>
                  </div>
                ) : (
                  <p className="ui-portal-muted">No upcoming appointments.</p>
                )}
              </section>

              <section className="ui-portal-card">
                <h2>Your details</h2>
                <dl className="ui-portal-dl">
                  <DetailRow label="Name" value={client?.name} />
                  <DetailRow label="Email" value={client?.email} />
                  <DetailRow label="Phone" value={client?.phone} />
                  <DetailRow
                    label="Address"
                    value={[client?.address, client?.postcode].filter(Boolean).join(', ')}
                  />
                </dl>
                {company?.businessEmail && (
                  <p className="ui-portal-fineprint">
                    Need to update something? Email{' '}
                    <a href={`mailto:${company.businessEmail}`}>{company.businessEmail}</a>
                  </p>
                )}
              </section>
            </div>
          </div>
        )}

        {!loading && !error && tab === 'quotes' && (
          <ListCard title="Your quotes" empty="No quotes yet.">
            {quotes.map((q) => (
              <div key={q.id} className="ui-portal-row">
                <span className="ui-portal-row-id">{q.quoteNumber}</span>
                <span className="ui-portal-row-main">{q.jobTitle}</span>
                <span className="ui-portal-row-amount">{money(q.totalAmount)}</span>
                <span className="ui-portal-row-meta">Valid until {formatDate(q.validUntil)}</span>
                <div className="ui-portal-row-actions">
                  <StatusPill status={q.status} />
                  {q.publicToken && (
                    <a
                      href={`/q/${q.publicToken}`}
                      target="_blank"
                      rel="noreferrer"
                      className="ui-portal-link"
                    >
                      Open
                    </a>
                  )}
                </div>
              </div>
            ))}
          </ListCard>
        )}

        {!loading && !error && tab === 'jobs' && (
          <ListCard title="Your jobs" empty="No jobs yet.">
            {jobs.map((j) => (
              <div key={j.id} className="ui-portal-row ui-portal-row-3col">
                <div className="ui-portal-row-main">
                  <p className="ui-portal-heading">{j.title || 'Untitled job'}</p>
                  <p className="ui-portal-muted">
                    {[j.site?.name || j.address, j.postcode].filter(Boolean).join(', ') || '—'}
                  </p>
                </div>
                <div>
                  <p>{formatDateTime(j.scheduledDate)}</p>
                  {j.assignedEngineer?.name && (
                    <p className="ui-portal-muted">Engineer: {j.assignedEngineer.name}</p>
                  )}
                </div>
                <StatusPill status={j.status} />
              </div>
            ))}
          </ListCard>
        )}

        {!loading && !error && tab === 'certificates' && (
          <ListCard title="Certificates" empty="No certificates issued yet.">
            {certs.map((c) => (
              <div key={c.id} className="ui-portal-row ui-portal-row-4col">
                <div className="ui-portal-row-main">
                  <p className="ui-portal-heading">{c.certificateNumber}</p>
                  <p className="ui-portal-muted">
                    {[c.site?.name, c.site?.postcode].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <span>{c.type.replace(/_/g, ' ')}</span>
                <span className="ui-portal-muted">
                  Issued {formatDate(c.issueDate)}
                  {c.expiryDate ? ` · Expires ${formatDate(c.expiryDate)}` : ''}
                </span>
                <StatusPill status={c.status} />
              </div>
            ))}
          </ListCard>
        )}

        {!loading && !error && tab === 'invoices' && (
          <ListCard title="Invoices" empty="No invoices yet.">
            {invoices.map((i) => (
              <div key={i.id} className="ui-portal-row ui-portal-row-5col">
                <span className="ui-portal-row-id">{i.invoiceNumber}</span>
                <span className="ui-portal-row-amount">{money(i.totalAmount)}</span>
                <span className="ui-portal-muted">Due {formatDate(i.dueDate)}</span>
                <span className="ui-portal-muted">
                  {i.paidDate ? `Paid ${formatDate(i.paidDate)}` : '—'}
                </span>
                <StatusPill status={i.status} />
              </div>
            ))}
          </ListCard>
        )}

        {!loading && !error && tab === 'sites' && (
          <SitesPanel
            sites={sites}
            onAdded={async () => setSites(await clientPortalService.listSites())}
          />
        )}
      </div>
    </PortalShell>
  );
}

function OverviewCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="ui-portal-stat-card">
      <p className="ui-portal-stat-title">{title}</p>
      <p className="ui-portal-stat-value">{value}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="ui-portal-dl-row">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

function ListCard({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode;
}) {
  const kids = React.Children.toArray(children);
  return (
    <div className="ui-portal-list">
      <h2 className="ui-portal-list-title">{title}</h2>
      {kids.length === 0 ? (
        <div className="ui-portal-empty">{empty}</div>
      ) : (
        <div className="ui-portal-list-rows">{kids}</div>
      )}
    </div>
  );
}

function SitesPanel({ sites, onAdded }: { sites: PortalSite[]; onAdded: () => Promise<void> }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Site name is required.');
      return;
    }
    setSaving(true);
    try {
      await clientPortalService.addSite({
        name: name.trim(),
        address: address.trim(),
        postcode: postcode.trim(),
        notes: notes.trim(),
      });
      setName('');
      setAddress('');
      setPostcode('');
      setNotes('');
      setShowForm(false);
      await onAdded();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ui-portal-stack">
      <div className="ui-portal-list-header">
        <h2 className="ui-portal-list-title">Your sites</h2>
        <Button
          variant={showForm ? 'secondary' : 'primary'}
          size="sm"
          leftIcon={!showForm ? <PlusIcon className="w-4 h-4" /> : undefined}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Cancel' : 'Add site'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="ui-portal-card">
          <div className="ui-portal-form-grid">
            <label>
              <span>Site name</span>
              <input
                className="ui-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Manchester office"
              />
            </label>
            <label>
              <span>Postcode</span>
              <input
                className="ui-input"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                placeholder="M1 1AA"
                style={{ textTransform: 'uppercase' }}
              />
            </label>
            <label className="ui-portal-form-span">
              <span>Address</span>
              <input
                className="ui-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </label>
            <label className="ui-portal-form-span">
              <span>Notes</span>
              <textarea
                className="ui-input ui-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </label>
          </div>
          {error && <p className="ui-field-message ui-field-message-error">{error}</p>}
          <div className="ui-portal-form-actions">
            <Button type="submit" variant="primary" size="sm" loading={saving} disabled={saving}>
              {saving ? 'Saving…' : 'Save site'}
            </Button>
          </div>
        </form>
      )}

      {sites.length === 0 ? (
        <div className="ui-portal-empty">No sites on file. Add your first site above.</div>
      ) : (
        <div className="ui-portal-list-rows">
          {sites.map((s) => (
            <div key={s.id} className="ui-portal-row">
              <div className="ui-portal-row-main">
                <p className="ui-portal-heading">{s.name}</p>
                <p className="ui-portal-muted">
                  {[s.address, s.postcode].filter(Boolean).join(', ') || '—'}
                </p>
                {s.notes && <p className="ui-portal-muted">{s.notes}</p>}
              </div>
              <span className="ui-portal-muted">Added {formatDate(s.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
